import React, { Fragment } from 'react';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import styled, { css, useTheme } from 'styled-components';
import {
  QuizChoiceLayoutWithSpace as BaseQuizChoiceLayoutWithSpace,
  QuizFooterContainer,
  QuizFooterInnerContainer,
  QuizInnerLayout as BaseQuizInnerLayout,
  QuizNumberAnswer,
  QuizTimer,
  QuizChoiceWithCheckbox,
  QuizChoiceWithRadio,
} from '@skooldio/skooldio-classroom-components';

import { Box, Flex, Text, SvgIcon, ButtonOutlined, ButtonFilled } from 'Components/Base';
import Loading from 'Components/Loading';
import { QUIZ_VARIANTS, EXAM_PAPER_ITEM_TYPE } from 'Components/Quiz/constant';
import { SESSION_STATUS } from 'Context/AssessmentContext';
import { useConfig } from 'Context/ConfigContext';
import { getTextColorByContrast } from 'Util/color';
import BaseHTMLView from 'Components/HTMLView';

import QuizQuestion from '../QuizQuestion';

import { Choice } from './QuizBox.container';
import QuizConnectionAnswer from './QuizConnectionAnswer';

const HTMLView = (props) => <BaseHTMLView {...props} isShowLoading />;

const getRadioBorderColor = (variant, theme) => {
  switch (variant) {
    case RADIO_VARIANT.CHECKED:
      return theme.colors.primary;
    case RADIO_VARIANT.CORRECT:
      return theme.colors.success;
    case RADIO_VARIANT.INCORRECT:
      return theme.colors.danger;
    case RADIO_VARIANT.DEFAULT:
    default:
      return theme.colors.greyscale.grey4;
  }
};

const Label = styled(Box)`
  ${({ clickable, variant, isAllowHighlight, theme }) => {
    const color = getRadioBorderColor(variant, theme);
    const isChecked = variant === 'CHECKED';
    return css`
      display: block;
      border: 2px solid ${color};
      cursor: ${clickable ? 'pointer' : 'default'};
      ${isChecked &&
      isAllowHighlight &&
      css`
        &&& {
          border-bottom: 2px solid ${color};
          box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.15);
        }
      `}
    `;
  }}
`;

Label.defaultProps = {
  px: 8,
  py: 5,
  as: 'label',
};

const QuizInnerLayout = styled(BaseQuizInnerLayout)`
  p:first-child {
    margin-top: 0;
  }
  p:last-child {
    margin-bottom: 0;
  }
`;

const RadioInput = styled('input')`
  -webkit-appearance: none;
  -moz-appearance: none;
  -ms-appearance: none;
  -o-appearance: none;
  appearance: none;
  display: none;
`;

const QuizChoiceLayoutWithSpace = styled(BaseQuizChoiceLayoutWithSpace)`
  & img {
    max-width: 100%;
  }
`;

/**
 * LatexWrapper fixes a MathJax rendering issue in Electron, where mjx-root elements
 * sometimes have a width of 0px, causing LaTeX content to be hidden. This forces
 * a minimal width to ensure proper rendering.
 */
const LatexWrapper = styled(Box)`
  .MathJax mjx-root {
    width: 1px !important; // Ensure LaTeX renders correctly in Electron
  }
`;

RadioInput.defaultProps = {
  type: 'radio',
};

const RADIO_VARIANT = {
  DEFAULT: 'DEFAULT',
  CHECKED: 'CHECKED',
  CORRECT: 'CORRECT',
  INCORRECT: 'INCORRECt',
};

const Radio = ({
  index,
  label,
  checked,
  onChange,
  onBlur,
  value,
  id,
  name,
  variant,
  clickable,
  isAllowHighlight,
  quizVariant,
  ...restProps
}) => {
  // formattedVariant is used to make this compatible to older version when there is no variant prop
  const formattedVariant = checked ? 'CHECKED' : variant;
  return (
    <Label
      px={6}
      py={5}
      as={'label'}
      border={`1px solid`}
      clickable={clickable}
      variant={formattedVariant}
      isAllowHighlight={isAllowHighlight}
      {...restProps}
    >
      <Flex>
        {index && <Text mr={1}>{index}</Text>}
        <HTMLView rawContent={label} />
      </Flex>
      <RadioInput
        id={id}
        checked={checked}
        name={name}
        onChange={onChange}
        onBlur={onBlur}
        value={value}
        disabled={!clickable}
      />
    </Label>
  );
};
Radio.VARIANT = RADIO_VARIANT;
Radio.propTypes = {
  index: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(RADIO_VARIANT)),
  label: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  clickable: PropTypes.bool,
  isAllowHighlight: PropTypes.bool,
};
Radio.defaultProps = {
  variant: RADIO_VARIANT.DEFAULT,
  clickable: true,
  isAllowHighlight: true,
};

// Pass choice which already connect to form container like formik to this component
const ChoiceBox = ({ variant, choices, renderChoice, questionId, labelVariant }) => {
  return (
    <QuizChoiceLayoutWithSpace>
      {choices &&
        choices.map((c) => {
          return renderChoice
            ? renderChoice({
                id: c.id,
                key: c.id,
                name: questionId,
                label: c.label,
                value: c.value,
                quizVariant: variant,
                labelVariant,
              })
            : null;
        })}
    </QuizChoiceLayoutWithSpace>
  );
};

const NumberAnswer = ({ precisionNumber, ...props }) => {
  return <QuizNumberAnswer precisionNumber={precisionNumber} {...props} />;
};

const ConnectionAnswer = ({ ...props }) => {
  return <QuizConnectionAnswer {...props} />;
};

const ButtonContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingContent = ({ isLoading, children }) =>
  isLoading ? (
    <Box px={7}>
      <Loading width={24} height={24} />
    </Box>
  ) : (
    <Fragment>{children}</Fragment>
  );

const QuizPaperItem = ({
  questionType,
  variant,
  choices,
  questionId,
  renderChoice,
  labelVariant,
  precisionNumber,
}) => {
  if (
    questionType === EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE ||
    questionType === EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE_DIFFERENT_SCORE
  ) {
    return (
      <ChoiceBox
        variant={variant}
        choices={choices}
        questionId={questionId}
        renderChoice={({ id, key, name, label, value, ...rest }) => {
          /**
           * Wrap the label with LatexWrapper to ensure proper LaTeX rendering.
           * This workaround is needed for cases where the first character isn't a
           * simple letter or number, which can cause rendering issues in Electron.
           */
          return (
            <LatexWrapper>
              <Choice
                id={id}
                key={key}
                name={name}
                label={label}
                value={value}
                as={QuizChoiceWithRadio}
                type={'radio'}
                {...rest}
              />
            </LatexWrapper>
          );
        }}
        labelVariant={labelVariant}
      />
    );
  } else if (
    questionType === EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT ||
    questionType === EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT_WITH_CORRECT_ANSWERS
  ) {
    return (
      <ChoiceBox
        variant={variant}
        choices={choices}
        questionId={questionId}
        renderChoice={({ id, key, name, label, value, ...rest }) => {
          /**
           * Wrap the label with LatexWrapper to ensure proper LaTeX rendering.
           * This workaround is needed for cases where the first character isn't a
           * simple letter or number, which can cause rendering issues in Electron.
           */
          return (
            <LatexWrapper>
              <Choice
                id={id}
                key={key}
                name={name}
                label={label}
                value={value}
                as={QuizChoiceWithCheckbox}
                type={'checkbox'}
                {...rest}
              />
            </LatexWrapper>
          );
        }}
        labelVariant={labelVariant}
      />
    );
  } else if (questionType === EXAM_PAPER_ITEM_TYPE.FILL_NUMBER) {
    return (
      <Field name={questionId} type={'text'} as={NumberAnswer} precisionNumber={precisionNumber} />
    );
  } else if (questionType === EXAM_PAPER_ITEM_TYPE.CONNECTION) {
    return <Field name={questionId} as={ConnectionAnswer} />;
  }
};

const ActionBox = ({
  variant,
  onNext,
  onBack,
  isFirstQuestion,
  isLastQuestion,
  nextButtonText,
  isSubmitting,
  ...restProps
}) => {
  const { colors } = useTheme();
  const dataTestIdPrefix = 'btn-quiz';

  const NextQuestionButton = (
    <ButtonContainer data-testid={`${dataTestIdPrefix}-next-question`}>
      <LoadingContent isLoading={isSubmitting}>
        <ButtonOutlined
          height={40}
          borderRadius={20}
          onClick={onNext}
          disabled={isSubmitting}
          fontWeight={600}
          py={0}
        >
          <Flex alignItems={'center'}>
            {nextButtonText || 'ข้อถัดไป'}
            <SvgIcon icon={SvgIcon.ICON_LIST.NEXT} size={20} />
          </Flex>
        </ButtonOutlined>
      </LoadingContent>
    </ButtonContainer>
  );

  const BackQuestionButton = (
    <ButtonContainer data-testid={`${dataTestIdPrefix}-previous-question`}>
      <LoadingContent isLoading={isSubmitting}>
        <ButtonOutlined
          borderRadius={20}
          height={40}
          onClick={onBack}
          disabled={isSubmitting}
          fontWeight={600}
          py={0}
        >
          <Flex alignItems={'center'}>
            <SvgIcon icon={SvgIcon.ICON_LIST.BACK} size={24} />
            <Box>ข้อก่อนหน้า</Box>
          </Flex>
        </ButtonOutlined>
      </LoadingContent>
    </ButtonContainer>
  );

  const SubmitExamButton = (
    <ButtonContainer data-testid={`${dataTestIdPrefix}-submit`}>
      <LoadingContent isLoading={isSubmitting}>
        <ButtonFilled
          type={'submit'}
          borderRadius={20}
          height={40}
          onClick={onNext}
          disabled={isSubmitting}
          fontWeight={600}
          textColor={getTextColorByContrast(colors.primary, 'text', 'white')}
          py={0}
        >
          {nextButtonText || 'ทวนคำตอบ'}
        </ButtonFilled>
      </LoadingContent>
    </ButtonContainer>
  );

  return (
    <QuizFooterInnerContainer>
      {!isFirstQuestion ? BackQuestionButton : <Box />}
      {isLastQuestion ? SubmitExamButton : NextQuestionButton}
    </QuizFooterInnerContainer>
  );
};

const QuizBoxView = ({
  choices,
  currentQuestionText,
  isFirstQuestion,
  isLastQuestion,
  isSubmitting,
  onBack,
  onNext,
  precisionNumber,
  question,
  questionId,
  questionNo,
  questionType = EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE,
  renderChoice,
  status,
  timeRemaining,
  variant,
}) => {
  const {
    classroom: { quiz },
  } = useConfig();
  const { colors } = useTheme();
  const { showAnswersConfirmation = false } = quiz?.gradedExam ?? {};
  const isTimeout = status === SESSION_STATUS.TIMEOUT || status === SESSION_STATUS.READY_TO_GRADE;
  const latexTranspiler = quiz?.latexTranspiler;
  return (
    <QuizInnerLayout>
      <Text fontFamily="contentReadable">
        {variant === QUIZ_VARIANTS.GRADED_EXAM && (
          <Flex justifyContent="space-between" pt={6}>
            <Text color={'greyscale.grey3'} fontFamily={'contentReadable'}>
              {currentQuestionText}
            </Text>
            <QuizTimer
              color={isTimeout ? colors.danger : colors.primary}
              isTimeout={isTimeout}
              timeRemaining={timeRemaining}
            />
          </Flex>
        )}
        <QuizQuestion
          question={question}
          questionNo={questionNo && `${questionNo}.`}
          pt={4}
          loadingComponent={() => <SvgIcon color={'primary'} icon={SvgIcon.ICON_LIST.LOADING} />}
          variant={latexTranspiler}
        />
        <QuizPaperItem
          questionType={questionType}
          variant={variant}
          choices={choices}
          questionId={questionId}
          renderChoice={renderChoice}
          labelVariant={latexTranspiler}
          precisionNumber={precisionNumber}
        />
      </Text>
      <QuizFooterContainer>
        <ActionBox
          variant={variant}
          onNext={onNext}
          onBack={onBack}
          isLastQuestion={isLastQuestion}
          isFirstQuestion={isFirstQuestion}
          style={{ height: '100%' }}
          isSubmitting={isSubmitting}
          nextButtonText={
            isLastQuestion && variant === QUIZ_VARIANTS.GRADED_EXAM && !showAnswersConfirmation
              ? 'ส่งคำตอบ'
              : undefined
          }
        />
      </QuizFooterContainer>
    </QuizInnerLayout>
  );
};

export {
  ChoiceBox,
  Radio,
  ActionBox,
  QuizBoxView,
  NumberAnswer,
  QuizPaperItem,
  QuizInnerLayout,
  QuizQuestion,
};
export default QuizBoxView;
